import { KeyValue } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFPersonalInformation, OtherNamePojo, RadioGroupButtonModel } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { DateFormatPipe } from '../service/dateFormatPipe';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, PIEvent, ProgressBarEvent, ValidateAndNavigateEvent, SkipToMainContentEvent } from '../service/events';
import { OtherNameComponent } from './other-name/other-name.component';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { MatDialog } from '@angular/material/dialog';
import { PersonalDialogComponent } from './personal-dialog/personal-dialog.component';
import { MatSelectChange } from '@angular/material/select';
import { NationalDialogComponent } from './national-dialog/national-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-personal-information',
  templateUrl: './personal-information.component.html',
  styleUrls: ['./personal-information.component.scss'],
  standalone: false
})
export class PersonalInformationComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  piData: AFPersonalInformation;
  showOtherName: boolean = false;
  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  submit: boolean = false;
  showWasWorkedInBoa: boolean = false;
  showHaveConnection: boolean = false;
  showPWC: boolean = false;
  requireMiddleNameLockbox: boolean = false;
  dt: DateFormatPipe;
  piGroup: UntypedFormGroup;
  jobCountryUSA: string = "United States of America";
  jobCountry: string = "United States of America"; //default
  ousInputDay: string = null;
  ousInputMonth: string = null;
  ousInputYear: string = null;

  @ViewChild("otherNames") otherNameComponent: OtherNameComponent;

  days: number[];
  years: number[];
  radioButtonGroupData: RadioGroupButtonModel[] = [];
  showRadioGender: boolean = false;
  showRadioPrevBank: boolean = false;
  showRadioHaveConnection: boolean = false;
  showRadioCurrentContractor: boolean = false;
  showRadioOtherName: boolean = false;
  showCountryOfBirth: boolean = false;
  otherNameText: string;
  hasUSGreenCard: boolean = false;
  isUSCitizen: boolean = false;
  jobCountryCode: string = '';
  fatherOrMotherSelection: KeyVal[];
  ousFlag: boolean = false;
  skinnyForm: boolean = false;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;
  regexDigit: RegExp = /^\d+$/;

  constructor(private userService: AuthService, private eventBus: EventService, private fb: UntypedFormBuilder, private _router: Router, dt: DateFormatPipe,
    private customValidator: CustomValidatorsService, public dialog: MatDialog, private _snackBar: MatSnackBar) {
    super(eventBus, _router);
    this.dt = dt;
    this.otherNameText = userService.getOtherNameText();
  }

  ngOnInit() {
    this.years = Array(200).fill(0).map((_, i) => 1900 + i);
    this.days = Array(31).fill(0).map((_, i) => 1 + i);
    this.jobCountryCode = this.userService.getJobCountry();
    this.skinnyForm = localStorage.getItem('skinnyForm') === '1';

    this.fatherOrMotherSelection = [
      { id: 1, value: "Mother's Name" },
      { id: 2, value: "Father's Name" }
    ];

    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    // invoke pop-up
    const dialogRef = this.dialog.open(PersonalDialogComponent, {
      ariaLabel: 'Personal Information Dialog',
      role: 'alertdialog',
      maxHeight: '40vh',
      maxWidth: '50vw',
      autoFocus: false,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      window.scrollTo(0, 0);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/personalInformation");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  get firstName() { return this.piGroup.get('firstName') };
  get middleName() { return this.piGroup.get('middleName') };
  get lastName() { return this.piGroup.get('lastName') };
  get fatherFirstName() { return this.piGroup.get('fatherFirstName') };
  get fatherMiddleName() { return this.piGroup.get('fatherMiddleName') };
  get fatherLastName() { return this.piGroup.get('fatherLastName') };
  get motherFirstName() { return this.piGroup.get('motherFirstName') };
  get motherMiddleName() { return this.piGroup.get('motherMiddleName') };
  get motherLastName() { return this.piGroup.get('motherLastName') };
  get gender() { return this.piGroup.get('gender') };
  get dateOfBirthMonth() { return this.piGroup.get('dateOfBirthMonth') };
  get dateOfBirthDay() { return this.piGroup.get('dateOfBirthDay') };
  get dateOfBirthYear() { return this.piGroup.get('dateOfBirthYear') };
  get email() { return this.piGroup.get('email') };
  get jobTitle() { return this.piGroup.get('jobTitle') };
  get primaryPhone() { return this.piGroup.get('primaryPhone') };
  get secondPhone() { return this.piGroup.get('secondPhone') };
  get ssn() { return this.piGroup.get('ssn') };
  get confirmSsn() { return this.piGroup.get('confirmSsn') };
  get prevBank() { return this.piGroup.get('prevBank') };
  get haveConnection() { return this.piGroup.get('haveConnection') };
  get currentContractor() { return this.piGroup.get('currentContractor') };
  get otherName() { return this.piGroup.get('otherName') };
  get suffix() { return this.piGroup.get('suffix') };
  get prevBankFromDate() { return this.piGroup.get('prevBankFromDate') };
  get prevBankEndDate() { return this.piGroup.get('prevBankEndDate') };
  get otherNameList() { return this.piGroup.get('otherNameList') };
  get verifyId() { return this.piGroup.get('verifyId') };
  get bookmarkUrl() { return this.piGroup.get('bookmarkUrl') };
  get countryOfBirth() { return this.piGroup.get('countryOfBirth') };
  get nationalId() { return this.piGroup.get('nationalId') };
  get inputOusMonth() { return this.piGroup.get('inputOusMonth') };
  get inputOusDay() { return this.piGroup.get('inputOusDay') };
  get inputOUsYear() { return this.piGroup.get('inputOUsYear') };
  get usGreenCard() { return this.piGroup.get('usGreenCard') };
  get usGreenCardDocumentNumber() { return this.piGroup.get('usGreenCardDocumentNumber') };
  get usGreenCardIssueMonth() { return this.piGroup.get('usGreenCardIssueMonth') };
  get usGreenCardIssueDay() { return this.piGroup.get('usGreenCardIssueDay') };
  get usGreenCardIssueYear() { return this.piGroup.get('usGreenCardIssueYear') };
  get usGreenCardExpiryMonth() { return this.piGroup.get('usGreenCardExpiryMonth') };
  get usGreenCardExpiryDay() { return this.piGroup.get('usGreenCardExpiryDay') };
  get usGreenCardExpiryYear() { return this.piGroup.get('usGreenCardExpiryYear') };
  get usGreenCardSSN() { return this.piGroup.get('usGreenCardSSN') };
  get usCitizen() { return this.piGroup.get('usCitizen') };
  get motherOrFather() { return this.piGroup.get('motherOrFather') };
  get pwc() { return this.piGroup.get('pwc') };
  get pwcStartDate() { return this.piGroup.get('pwcStartDate') };
  get pwcEndDate() { return this.piGroup.get('pwcEndDate') };
  get cityBorn() { return this.piGroup.get('cityBorn') };
  get motherMaidenName() { return this.piGroup.get('motherMaidenName') };
  get nationalIdCountry() { return this.piGroup.get('nationalIdCountry') };

  subscribeEvents() {
    this.userService.getPersonalInformation().subscribe({
      next: (data: AFPersonalInformation) => {
        this.piData = data;

        if (this.piData.jobCountryValue) { //default is United States of America
          this.jobCountry = this.piData.jobCountryValue;
        }

        this.ousFlag = !(this.jobCountry === this.jobCountryUSA || (this.jobCountryCode === 'IND' && this.userService.getJobSubCountry() === 'GBS'));

        //US applicants only with confirmation ssn
        if (this.jobCountry === this.jobCountryUSA) {
          if (data.ssn === 'true') {
            this.piData.ssn = '000000000';
          } else {
            this.piData.ssn = '';
          }
        }

        if (this.ousFlag) {
          this.piData.dateOfBirthMonth = '';
          this.piData.dateOfBirthDay = '';
          this.piData.dateOfBirthYear = '';

          this.piData.inputOusMonth = '0';
          this.piData.inputOusDay = '0';
          this.piData.inputOUsYear = '0';

          if (!this.skinnyForm) {
            this.nationalId.setValidators([Validators.required, Validators.maxLength(100)]);
            this.nationalIdCountry.setValidators([Validators.required]);
          }

          this.ssn.setValidators(null);
          this.ssn.setErrors(null);
          this.confirmSsn.setValidators(null);
          this.confirmSsn.setErrors(null);

          this.gender.setValidators(null);
          this.gender.setErrors(null);
          this.currentContractor.setValidators(null);
          this.currentContractor.setErrors(null);
          this.otherName.setValidators(null);
          this.otherName.setErrors(null);

          if (this.jobCountryCode === 'IND') {
            this.motherOrFather.setValidators(null);
            this.motherOrFather.setErrors(null);
          }

          this.pwc.setValidators(null);
          this.pwc.setErrors(null);

          this.piGroup.updateValueAndValidity();

        } else if (this.jobCountry !== this.jobCountryUSA) {
          this.piData.dateOfBirthMonth = '';
          this.piData.dateOfBirthDay = '';
          this.piData.dateOfBirthYear = '';

          this.piData.inputOusMonth = '0';
          this.piData.inputOusDay = '0';
          this.piData.inputOUsYear = '0';

          this.pwc.setValidators(null);
          this.pwc.setErrors(null);

          this.ssn.setValidators(null);
          this.ssn.setErrors(null);
          this.confirmSsn.setValidators(null);
          this.confirmSsn.setErrors(null);

          this.usGreenCard.setValidators(Validators.required);
          this.usCitizen.setValidators(Validators.required);
          this.hasUSGreenCard = (this.piData.usGreenCard === '1');

          if (this.hasUSGreenCard) {
            this.usGreenCardDocumentNumber.setValidators([Validators.required, Validators.maxLength(25)]);
            this.usGreenCardIssueMonth.setValidators(Validators.required);
            this.usGreenCardIssueDay.setValidators(Validators.required);
            this.usGreenCardIssueYear.setValidators(Validators.required);
            this.usGreenCardExpiryMonth.setValidators(Validators.required);
            this.usGreenCardExpiryDay.setValidators(Validators.required);
            this.usGreenCardExpiryYear.setValidators(Validators.required);
            this.usGreenCardSSN.setValidators([Validators.required, Validators.maxLength(25)]);
          }

          if (this.jobCountryCode !== 'IND') {
            this.gender.setValidators(null);
            this.gender.setErrors(null);
          }

          this.piGroup.updateValueAndValidity();
        }

        this.piGroup.patchValue(this.piData);
        this.showOtherName = (this.piData.otherName === "1");
        this.showWasWorkedInBoa = (this.piData.prevBank === "1");
        this.showHaveConnection = (this.piData.haveConnection === "1");
        this.requireMiddleNameLockbox = (this.piData.lockBoxFlag !== "-1");
        this.showPWC = (this.piData.pwc === '1');

        if (this.showPWC) {
          this.pwcStartDate.setValidators([Validators.required, Validators.maxLength(25)]);
          this.pwcEndDate.setValidators([Validators.required, Validators.maxLength(25)]);
          this.piGroup.updateValueAndValidity();
        }

        if (this.requireMiddleNameLockbox) {
          this.middleName.setValidators([this.customValidator.validateRequiredField]);
        }

        if (this.piData.applicantType && this.piData.applicantType !== 'Internal' && !this.ousFlag) {
          this.prevBank.setValidators([Validators.required]);
        }

        if (this.piData.displayPepQuestion && !this.ousFlag) {
          this.haveConnection.setValidators([Validators.required]);
        }

        if ((null !== this.piData?.countryRules?.countryOfBirth
          && this.piData?.countryRules?.countryOfBirth === 1) || (this.jobCountry !== this.jobCountryUSA && this.jobCountryCode !== 'IND' && !this.skinnyForm)) {
          this.showCountryOfBirth = true;
          this.countryOfBirth.setValidators([Validators.required]);
        } else {
          this.countryOfBirth.setValidators(null);
        }

        if (!(this.ousFlag || this.skinnyForm)) {
          this.piGroup.valueChanges.subscribe(() => {
            let progress = this.getPercentage(this.piGroup, this.otherNameComponent.otherNameform);
            this.eventBus.emit(new ProgressBarEvent({ id: progress }));
          });

          this.otherNameComponent.otherNameform.valueChanges.subscribe(() => {
            let progress = this.getPercentage(this.piGroup, this.otherNameComponent.otherNameform);
            this.eventBus.emit(new ProgressBarEvent({ id: progress }));
          });
        } else {
          let progress = this.getPercentageSkinnyForm(this.piGroup);
          this.eventBus.emit(new ProgressBarEvent({ id: progress }));

          this.piGroup.valueChanges.subscribe(() => {
            let progress = this.getPercentageSkinnyForm(this.piGroup);
            this.eventBus.emit(new ProgressBarEvent({ id: progress }));
          });
        }

        /*
        radio button meta data
        */
        //gender
        let radioGroupMetaData = new RadioGroupButtonModel();
        radioGroupMetaData.groupName = "radioGender";
        radioGroupMetaData.groupValue = this.piData.gender;
        this.radioButtonGroupData.push(radioGroupMetaData);
        //prevBank
        radioGroupMetaData = new RadioGroupButtonModel();
        radioGroupMetaData.groupName = "radioPrevBank";
        radioGroupMetaData.groupValue = this.piData.prevBank;
        this.radioButtonGroupData.push(radioGroupMetaData);
        //haveConnection
        radioGroupMetaData = new RadioGroupButtonModel();
        radioGroupMetaData.groupName = "radioHaveConnection";
        radioGroupMetaData.groupValue = this.piData.haveConnection;
        this.radioButtonGroupData.push(radioGroupMetaData);
        //currentContractor
        radioGroupMetaData = new RadioGroupButtonModel();
        radioGroupMetaData.groupName = "radioCurrentContractor";
        radioGroupMetaData.groupValue = this.piData.currentContractor;
        this.radioButtonGroupData.push(radioGroupMetaData);
        //otherName
        radioGroupMetaData = new RadioGroupButtonModel();
        radioGroupMetaData.groupName = "radioOtherName";
        radioGroupMetaData.groupValue = this.piData.otherName;
        this.radioButtonGroupData.push(radioGroupMetaData);

        this.eventBus.emit(new PIEvent({ piData: data }));

      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/personalInformation') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitPersonalInformation((this.piGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitPersonalInformation((this.piGroup.value), event);
          } else if (this.validate()) {
            this.submitPersonalInformation((this.piGroup.value), event);
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/personalInformation") {
          let mainContentElement = document.querySelector("#main-content"); //check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true); //align top
          }
        }
      }
    );
  }

  submitPersonalInformation(personalInformation: AFPersonalInformation, event) {
    if (!this.ousFlag) {
      personalInformation.otherNameList = this.otherNameComponent.otherNameform.value['otherNameList'];
    }
    personalInformation.jobCountryValue = this.jobCountry;
    personalInformation.inputOusMonth = this.ousInputMonth;
    personalInformation.inputOusDay = this.ousInputDay;
    personalInformation.inputOUsYear = this.ousInputYear;

    if (personalInformation.nationalIdCountry != null && personalInformation.nationalIdCountry !== '') {
      personalInformation.nationalIdCountry = Number(personalInformation.nationalIdCountry);
    }

    if (personalInformation.inputOUsYear === '0') {
      personalInformation.inputOUsYear = null;
    }

    if (personalInformation.inputOusDay === '0') {
      personalInformation.inputOusDay = null;
    }

    if (personalInformation.inputOusMonth === '0') {
      personalInformation.inputOusMonth = null;
    }

    if (this.pwc?.value) {
      personalInformation.pwc = Number(this.pwc.value);
    }

    this.userService.submitPersonalInformation(personalInformation).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (exception: any) => {
        // Add check for ApplicationException vs Exception
        if (exception instanceof HttpErrorResponse) {
          let message: string = "Error occurred while saving Applicant Form";
          if (exception.error.errors && exception.error.errors.length > 0) {
            message = exception.error.errors[0].defaultMessage;
          } else if (exception.error.message) {
            message = exception.error.message;
          }

          this._snackBar.open("Error: " + message, "Close", {
            duration: 5000,
            horizontalPosition: "center",
            verticalPosition: "top"
          });
        } else {
          this._snackBar.open("Error: " + exception.message, "Close", {
            duration: 5000,
            horizontalPosition: "center",
            verticalPosition: "top"
          });
        }
      }
    });
  }

  createGroup() {
    this.piGroup = this.fb.group({
      verifyId: [this.userService.getVerifyId(), []],
      bookmarkUrl: [''],
      firstName: ['', [Validators.required, Validators.maxLength(50), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      middleName: ['', [Validators.maxLength(50), Validators.pattern(this.regexName)]],
      lastName: ['', [Validators.required, Validators.maxLength(50), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      fatherFirstName: [''],
      fatherMiddleName: [''],
      fatherLastName: [''],
      motherFirstName: [''],
      motherMiddleName: [''],
      motherLastName: [''],
      suffix: ['', [Validators.maxLength(50)]],
      gender: ['', [Validators.required]],
      motherMaidenName: ['', [Validators.maxLength(100), Validators.pattern(this.regexName)]],
      cityBorn: ['', [Validators.maxLength(100), Validators.pattern(this.regexName)]],
      dateOfBirthMonth: ['', [Validators.required]],
      dateOfBirthDay: ['', [Validators.required]],
      dateOfBirthYear: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      jobTitle: [''],
      primaryPhone: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(25), this.customValidator.validateEndSpace]],
      secondPhone: ['', [Validators.minLength(9), Validators.maxLength(25), this.customValidator.validateEndSpace]],
      ssn: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(9), this.customValidator.validateDigits]],
      confirmSsn: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(9), this.customValidator.validateDigits]],
      prevBank: [''],
      haveConnection: [''],
      currentContractor: ['', [Validators.required]],
      prevBankFromDate: ['', [Validators.maxLength(100)]],
      prevBankEndDate: ['', [Validators.maxLength(100)]],
      personNumber: ['', [Validators.maxLength(60), Validators.pattern(this.regexDigit)]],
      bankAffiliate: [''],
      otherName: ['', [Validators.required]],
      otherNameList: new Array<OtherNamePojo>(),
      countryOfBirth: [''],
      nationalId: [''],
      nationalIdCountry: [''],
      inputOusMonth: [''],
      inputOusDay: [''],
      inputOUsYear: [''],
      usCitizen: [''],
      usGreenCard: [''],
      usGreenCardDocumentNumber: [''],
      usGreenCardIssueMonth: [''],
      usGreenCardIssueDay: [''],
      usGreenCardIssueYear: [''],
      usGreenCardExpiryMonth: [''],
      usGreenCardExpiryDay: [''],
      usGreenCardExpiryYear: [''],
      usGreenCardSSN: [''],
      pwc: ['', [Validators.required]],
      pwcStartDate: [''],
      pwcEndDate: ['']
    });

    if (this.jobCountryCode === 'IND') {
      this.piGroup.addControl('motherOrFather', this.fb.control('', [Validators.required]));
    }

    this.piGroup.addValidators([dateValidator()]);
  }

  onFocusOutMainContent() {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  compareSelectValueFn(f1: any, f2: any) {
    return f1 == f2;
  }

  radioOnChange(event: MatRadioChange) {
    const radioGroupName = event.source.name;
    const radioGroupChgValue = event.value;
    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  radioOnFocusOut(event: any) {
    const radioGroupName = event.target.name;
    let showError: boolean = false;
    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          if (radioGroup.groupValue != "0" && radioGroup.groupValue != "1") {
            showError = true;
          }
          if (radioGroup.groupName == "radioGender") {
            this.showRadioGender = showError;
          } else if (radioGroup.groupName == "radioPrevBank") {
            this.showRadioPrevBank = showError;
          } else if (radioGroup.groupName == "radioHaveConnection") {
            this.showRadioHaveConnection = showError;
          } else if (radioGroup.groupName == "radioCurrentContractor") {
            this.showRadioCurrentContractor = showError;
          } else { //radioGroup.groupName == "radioOtherName"
            this.showRadioOtherName = showError;
          }
          break;
        }
      }
    }
  }

  radioOnFocus(event: any) {
    const radioGroupName = event.target.name;
    if (radioGroupName == "radioGender") {
      this.showRadioGender = false;
    } else if (radioGroupName == "radioPrevBank") {
      this.showRadioPrevBank = false;
    } else if (radioGroupName == "radioHaveConnection") {
      this.showRadioHaveConnection = false;
    } else if (radioGroupName == "radioCurrentContractor") {
      this.showRadioCurrentContractor = false;
    } else { //radioGroup.groupName == "radioOtherName"
      this.showRadioOtherName = false;
    }
  }

  validate() {
    this.submit = true;
    this.piGroup.markAllAsTouched();
    this.userService.setFocus(this.piGroup);
    if (this.showOtherName) {
      this.otherNameComponent.otherNameform.markAllAsTouched();
    }

    if (this.piGroup.invalid) {
      return false;
    }

    if (this.showOtherName && this.otherNameComponent.validate()) {
      return false;
    }

    return true;
  }

  onOtherNameChange(mrChange: MatRadioChange) {
    this.showOtherName = (mrChange.value === "1");

    const radioGroupName = mrChange.source.name;
    const radioGroupChgValue = mrChange.value;
    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }

    let progress = this.getPercentage(this.piGroup, this.otherNameComponent.otherNameform);
    this.eventBus.emit(new ProgressBarEvent({ id: progress }));
  }

  fatherOrMother(event: MatSelectChange) {
    if (event.value === 1) { //Mother
      this.fatherFirstName.setValidators(null);
      this.fatherFirstName.setErrors(null);
      this.fatherLastName.setValidators(null);
      this.fatherLastName.setErrors(null);

      this.motherFirstName.setValidators([Validators.required, Validators.maxLength(100)]);
      this.motherLastName.setValidators([Validators.required, Validators.maxLength(100)]);
      this.motherFirstName.updateValueAndValidity();
      this.motherLastName.updateValueAndValidity();

    } else { //Father
      this.motherFirstName.setValidators(null);
      this.motherFirstName.setErrors(null);
      this.motherLastName.setValidators(null);
      this.motherLastName.setErrors(null);

      this.fatherFirstName.setValidators([Validators.required, Validators.maxLength(100)]);
      this.fatherLastName.setValidators([Validators.required, Validators.maxLength(100)]);
      this.fatherFirstName.updateValueAndValidity();
      this.fatherLastName.updateValueAndValidity();
    }
    this.piGroup.updateValueAndValidity();
  }

  onWasWorkedInBoaFlagChange(mrChange: MatRadioChange) {
    this.showWasWorkedInBoa = (mrChange.value === "1");
    const radioGroupName = mrChange.source.name;
    const radioGroupChgValue = mrChange.value;

    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  hasUSGreenCardChange(mrChange: MatRadioChange) {
    this.hasUSGreenCard = (mrChange.value === "1");
    const radioGroupName = mrChange.source.name;
    const radioGroupChgValue = mrChange.value;

    if (mrChange.value === "1") {
      this.usGreenCardDocumentNumber.setValidators([Validators.required, Validators.maxLength(25)]);
      this.usGreenCardIssueMonth.setValidators(Validators.required);
      this.usGreenCardIssueDay.setValidators(Validators.required);
      this.usGreenCardIssueYear.setValidators(Validators.required);
      this.usGreenCardExpiryMonth.setValidators(Validators.required);
      this.usGreenCardExpiryDay.setValidators(Validators.required);
      this.usGreenCardExpiryYear.setValidators(Validators.required);
      this.usGreenCardSSN.setValidators([Validators.required, Validators.maxLength(25)]);
      this.piGroup.updateValueAndValidity();
    } else if (mrChange.value === "2") {
      this.usGreenCardDocumentNumber.setValidators(null);
      this.usGreenCardDocumentNumber.setErrors(null);
      this.usGreenCardIssueMonth.setValidators(null);
      this.usGreenCardIssueMonth.setErrors(null);
      this.usGreenCardIssueDay.setValidators(null);
      this.usGreenCardIssueDay.setErrors(null);
      this.usGreenCardIssueYear.setValidators(null);
      this.usGreenCardIssueYear.setErrors(null);
      this.usGreenCardExpiryMonth.setValidators(null);
      this.usGreenCardExpiryMonth.setErrors(null);
      this.usGreenCardExpiryDay.setValidators(null);
      this.usGreenCardExpiryDay.setErrors(null);
      this.usGreenCardExpiryYear.setValidators(null);
      this.usGreenCardExpiryYear.setErrors(null);
      this.usGreenCardSSN.setValidators(null);
      this.usGreenCardSSN.setErrors(null);
      this.piGroup.updateValueAndValidity();
    }

    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  pwcChange(mrChange: MatRadioChange) {
    this.showPWC = (mrChange.value === "1");
    const radioGroupName = mrChange.source.name;
    const radioGroupChgValue = mrChange.value;

    if (mrChange.value === '1') {
      this.pwcStartDate.markAsUntouched();
      this.pwcEndDate.markAsUntouched();
      this.pwcStartDate.setValidators([Validators.required, Validators.maxLength(25)]);
      this.pwcEndDate.setValidators([Validators.required, Validators.maxLength(25)]);
      this.pwcStartDate.updateValueAndValidity();
      this.pwcEndDate.updateValueAndValidity();
    } else if (mrChange.value === '0') {
      this.pwcStartDate.setValidators(null);
      this.pwcStartDate.setErrors(null);
      this.pwcEndDate.setValidators(null);
      this.pwcEndDate.setErrors(null);
      this.pwcStartDate.reset();
      this.pwcEndDate.reset();
      this.pwcStartDate.markAsPristine();
      this.pwcEndDate.markAsPristine();
      this.pwcStartDate.updateValueAndValidity();
      this.pwcEndDate.updateValueAndValidity();
    }

    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  hasUSCitizenChange(mrChange: MatRadioChange) {
    this.isUSCitizen = (mrChange.value === "1");
    const radioGroupName = mrChange.source.name;
    const radioGroupChgValue = mrChange.value;

    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  getPercentage(container: UntypedFormGroup, otherNameForm: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;
    let channelArray: Array<string>;

    if (this.piData.applicantType && this.piData.applicantType !== 'Internal') {
      channelArray = ['middleName', 'suffix', 'secondPhone', 'prevBankFromDate', 'prevBankEndDate', 'personNumber', 'bankAffiliate', 'otherMiddleName'];
    } else {
      channelArray = ['middleName', 'suffix', 'secondPhone', 'prevBankFromDate', 'prevBankEndDate', 'personNumber', 'bankAffiliate', 'otherMiddleName', 'prevBank'];
    }

    if (this.usGreenCard && this.usGreenCard.value !== '1') {
      channelArray = channelArray.concat(['usGreenCardDocumentNumber', 'usGreenCardIssueMonth', 'usGreenCardIssueDay', 'usGreenCardIssueYear', 'usGreenCardExpiryMonth', 'usGreenCardExpiryDay', 'usGreenCardExpiryYear', 'usGreenCardSSN']);
    }

    if (this.pwc && this.pwc.value !== '1') {
      channelArray = channelArray.concat(['pwcStartDate', 'pwcEndDate']);
    }

    for (let controlKey in container.controls) {
      if (channelArray.some(x => x === controlKey)) {
        continue;
      }

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }

        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    if (this.showOtherName && !this.ousFlag) {
      for (let container of otherNameForm.get('otherNameList')['controls']) {
        for (let controlKey in container.controls) {
          if (channelArray.some(x => x === controlKey)) {
            continue;
          }

          if (container.controls.hasOwnProperty(controlKey)) {
            if (container.controls[controlKey].validator != null) {
              totalCount += container.controls[controlKey].validator.length;
            }
            if (container.controls[controlKey].errors) {
              errorCount += Object.keys(container.controls[controlKey].errors).length;
            }
          }
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  getPercentageSkinnyForm(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    let channelArray: Array<string>;
    channelArray = ['middleName', 'suffix', 'secondPhone', 'prevBankFromDate', 'prevBankEndDate', 'personNumber', 'bankAffiliate', 'otherMiddleName',
      'ssn', 'confirmSsn', 'haveConnection', 'prevBank', 'gender', 'currentContractor', 'otherName', 'otherNameList', 'nationalId', 'inputOusMonth',
      'inputOusDay', 'inputOUsYear', 'nationalId', 'usCitizen', 'usGreenCard', 'motherOrFather', 'fatherFirstName', 'fatherMiddleName', 'fatherLastName',
      'motherFirstName', 'motherMiddleName', 'motherLastName', 'jobTitle', 'usGreenCardDocumentNumber', 'usGreenCardIssueMonth', 'usGreenCardIssueDay',
      'usGreenCardIssueYear', 'usGreenCardExpiryMonth', 'usGreenCardExpiryDay', 'usGreenCardExpiryYear', 'usGreenCardSSN', 'pwc', 'motherMaidenName', 'countryOfBirth', 'cityBorn'];

    for (let controlKey in container.controls) {
      if (channelArray.some(x => x === controlKey)) {
        continue;
      }

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  descOrder = (a, b) => {
    if (Number(a.key) < Number(b.key)) return b.key;
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  dropDownScreenReaderMonth(): string {
    if (this.piGroup.controls.dateOfBirthMonth.value) {
      return 'Selected ' + this.piData.monthLookup[Object.keys(this.piData.monthLookup).find(e => e === this.piGroup.controls.dateOfBirthMonth.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderDay(): string {
    if (this.piGroup.controls.dateOfBirthDay.value) {
      return 'Selected ' + this.piGroup.controls.dateOfBirthDay.value;
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderYear(): string {
    if (this.piGroup.controls.dateOfBirthYear.value) {
      return 'Selected ' + this.piGroup.controls.dateOfBirthYear.value;
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderAffiliate(): string {
    if (this.piGroup.controls.bankAffiliate.value) {
      return 'Selected ' + this.piData.bankAffiliatesLookup[Object.keys(this.piData.bankAffiliatesLookup).find(e => e === this.piGroup.controls.bankAffiliate.value)];
    } else {
      return 'blank';
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  handleSelection(event: MatSelectChange, element: string) {
    if (element === 'month') {
      this.ousInputMonth = event.value;
    } else if (element === 'day') {
      this.ousInputDay = event.value;
    } else {
      this.ousInputYear = event.value;
    }
    event.source.value = "0";
  }

  getIDDocument(): void {
    this.dialog.open(NationalDialogComponent, {
      ariaLabel: 'National ID Summary Dialog',
      role: 'alertdialog',
      width: '90vw',
      maxWidth: '650px',
      height: '90vh',
      maxHeight: '650px',
      autoFocus: false,
      disableClose: true
    });
  }
}

export function dateValidator(): ValidatorFn {
  return (control: UntypedFormGroup): ValidationErrors | null => {
    const errors: ValidationErrors = {};

    /* added date validation for if the user chooses to supply a date */
    const prevBankEndDate = control.get('prevBankEndDate');
    const prevBankFromDate = control.get('prevBankFromDate');

    if (prevBankFromDate.value && prevBankEndDate.value && !prevBankFromDate.errors && !prevBankEndDate.errors) {
      if (prevBankFromDate.value > prevBankEndDate.value) {
        prevBankEndDate.setErrors({ noMatch: true });
      } else {
        prevBankEndDate.setErrors(null);
      }
    }

    const pwc = control.get('pwc');

    if (pwc && pwc.value === "1") {
      const pwcEndDate = control.get('pwcEndDate');
      const pwcStartDate = control.get('pwcStartDate');

      if (pwcStartDate.value && pwcEndDate.value && !pwcStartDate.errors && !pwcEndDate.errors) {
        if (pwcStartDate.value > pwcEndDate.value) {
          pwcEndDate.setErrors({ noMatch: true });
        } else {
          pwcEndDate.setErrors(null);
        }
      }
    }

    /* added date of birth validation */
    const dateOfBirthYear = control.get('dateOfBirthYear');
    const dateOfBirthMonth = control.get('dateOfBirthMonth');
    const dateOfBirthDay = control.get('dateOfBirthDay');
    let now: Date = new Date();
    let inputDate: Date = new Date(dateOfBirthYear.value, (dateOfBirthMonth.value - 1), dateOfBirthDay.value);

    now.setHours(0, 0, 0, 0);

    if (inputDate > now) {
      dateOfBirthYear.setErrors({ futureDate: true });
      dateOfBirthMonth.setErrors({ futureDate: true });
      dateOfBirthDay.setErrors({ futureDate: true });
    } else {
      if (dateOfBirthYear.value) { dateOfBirthYear.setErrors(null); }
      if (dateOfBirthMonth.value) { dateOfBirthMonth.setErrors(null); }
      if (dateOfBirthDay.value) { dateOfBirthDay.setErrors(null); }
    }

    return Object.keys(errors).length ? errors : null;
  };
}
export interface KeyVal {
  id: number;
  value: string;
}